import {
  SALON_FONT_KEYS,
  SALON_FONT_PRESETS,
} from './salonPublicTheme';


const deepFreeze = (value) => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach(deepFreeze);
  }
  return value;
};

export const SALON_SECTION_STYLE_KEYS = Object.freeze([
  'header',
  'gallery',
  'services',
  'team',
  'location',
  'opening_hours',
  'reviews',
]);

export const SALON_TEXT_STYLE_FIELDS = Object.freeze([
  'font_key',
  'size',
  'weight',
  'italic',
  'color',
  'alignment',
  'transform',
  'letter_spacing',
]);

export const SALON_TEXT_STYLE_REGISTRY = deepFreeze({
  font_key: ['inherit', ...SALON_FONT_KEYS],
  size: ['xs', 'sm', 'md', 'lg', 'xl'],
  weight: [400, 500, 600, 700, 800],
  italic: [false, true],
  alignment: ['start', 'center', 'end'],
  transform: ['none', 'uppercase'],
  letter_spacing: ['normal', 'wide'],
});

export const DEFAULT_SALON_TITLE_STYLE = deepFreeze({
  font_key: 'inherit',
  size: 'lg',
  weight: 600,
  italic: false,
  color: 'inherit',
  alignment: 'start',
  transform: 'none',
  letter_spacing: 'normal',
});

export const DEFAULT_SALON_CONTENT_STYLE = deepFreeze({
  font_key: 'inherit',
  size: 'md',
  weight: 400,
  italic: false,
  color: 'inherit',
  alignment: 'start',
  transform: 'none',
  letter_spacing: 'normal',
});

export const DEFAULT_SALON_BUTTON_STYLE = deepFreeze({
  appearance: 'filled',
  shape: 'rounded',
  size: 'standard',
  background_color: 'inherit',
  border_color: 'inherit',
  text_color: 'inherit',
  font_key: 'inherit',
  text_size: 'md',
  weight: 600,
  italic: false,
  transform: 'none',
  letter_spacing: 'normal',
});

export const SALON_SERVICE_COLUMN_OPTIONS = Object.freeze([2, 3, 4]);

export const SALON_SECTION_VISIBILITY_KEYS = Object.freeze([
  'gallery',
  'team',
  'location',
  'opening_hours',
  'reviews',
]);

export const DEFAULT_SALON_SECTION_VISIBILITY = deepFreeze(
  Object.fromEntries(
    SALON_SECTION_VISIBILITY_KEYS.map((key) => [key, true]),
  ),
);

export const DEFAULT_SALON_PUBLIC_LAYOUT = deepFreeze({
  version: 3,
  buttons: { ...DEFAULT_SALON_BUTTON_STYLE },
  header: {
    composition: 'horizontal',
    surface: 'transparent',
    behavior: 'static',
  },
  content: {
    alignment: 'start',
    services: 'list',
    service_columns: 3,
    sidebar: 'end',
  },
  section_styles: Object.fromEntries(SALON_SECTION_STYLE_KEYS.map((key) => [
    key,
    {
      title: { ...DEFAULT_SALON_TITLE_STYLE },
      content: { ...DEFAULT_SALON_CONTENT_STYLE },
    },
  ])),
  visibility: { ...DEFAULT_SALON_SECTION_VISIBILITY },
});

export const SALON_PUBLIC_LAYOUT_REGISTRY = deepFreeze({
  buttons: {
    appearance: ['filled', 'outlined', 'soft'],
    shape: ['square', 'rounded', 'pill'],
    size: ['compact', 'standard', 'large'],
  },
  header: {
    composition: ['horizontal', 'centered', 'compact'],
    surface: ['light', 'brand', 'transparent'],
    behavior: ['static', 'sticky'],
  },
  content: {
    alignment: ['start', 'center', 'end'],
    services: ['grid', 'list'],
    sidebar: ['start', 'end', 'stacked'],
  },
});

const BUTTON_STYLE_REGISTRY = deepFreeze({
  font_key: ['inherit', ...SALON_FONT_KEYS],
  text_size: ['xs', 'sm', 'md', 'lg', 'xl'],
  weight: [400, 500, 600, 700, 800],
  italic: [false, true],
  transform: ['none', 'uppercase'],
  letter_spacing: ['normal', 'wide'],
});

const ALLOWED_TOP_LEVEL_KEYS = Object.freeze([
  'version',
  'buttons',
  'header',
  'content',
  'section_styles',
  'visibility',
]);

const BUTTON_APPEARANCE_TOKENS = deepFreeze({
  filled: { variant: 'contained', elevation: 0 },
  outlined: { variant: 'outlined', elevation: 0 },
  soft: { variant: 'text', elevation: 0 },
});

const BUTTON_SHAPE_TOKENS = deepFreeze({
  square: { borderRadius: '0px' },
  rounded: { borderRadius: '12px' },
  pill: { borderRadius: '999px' },
});

const BUTTON_SIZE_TOKENS = deepFreeze({
  compact: {
    minHeight: '44px',
    paddingBlock: '6px',
    paddingInline: '14px',
    iconSize: '44px',
  },
  standard: {
    minHeight: '48px',
    paddingBlock: '10px',
    paddingInline: '20px',
    iconSize: '48px',
  },
  large: {
    minHeight: '56px',
    paddingBlock: '14px',
    paddingInline: '26px',
    iconSize: '56px',
  },
});

const TEXT_SIZE_TOKENS = Object.freeze({
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
});
const LETTER_SPACING_TOKENS = Object.freeze({
  normal: 'normal',
  wide: '0.08em',
});
const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

const isObject = (value) => (
  value !== null && !Array.isArray(value) && typeof value === 'object'
);

const copyDefaultLayout = () => JSON.parse(
  JSON.stringify(DEFAULT_SALON_PUBLIC_LAYOUT),
);

const addUnknownFields = (errors, value, allowed) => {
  const unknown = Object.keys(value)
    .filter((key) => !allowed.includes(key))
    .sort();
  if (unknown.length) errors.unknown_fields = unknown;
};

const normalizeColor = (value) => {
  if (value === 'inherit') return { value, error: '' };
  if (typeof value === 'string' && HEX_COLOR_RE.test(value)) {
    return { value: value.toUpperCase(), error: '' };
  }
  return {
    value: null,
    error: "Enter 'inherit' or a valid hexadecimal color.",
  };
};

const validateChoiceFields = (draft, defaults, registry, message) => {
  if (!isObject(draft)) {
    return {
      value: null,
      errors: { non_field_errors: ['This section must be an object.'] },
    };
  }
  const errors = {};
  addUnknownFields(errors, draft, Object.keys(defaults));
  const value = { ...defaults, ...draft };
  Object.entries(registry).forEach(([field, choices]) => {
    if (!choices.includes(value[field])) errors[field] = [message];
  });
  return { value, errors };
};

const validateTextStyle = (draft, defaults) => {
  const result = validateChoiceFields(
    draft,
    defaults,
    SALON_TEXT_STYLE_REGISTRY,
    'Select a supported style value.',
  );
  if (!result.value) return result;
  const color = normalizeColor(result.value.color);
  if (color.error) result.errors.color = [color.error];
  else result.value.color = color.value;
  return result;
};

const validateButtons = (draft) => {
  if (!isObject(draft)) {
    return {
      value: null,
      errors: { non_field_errors: ['This section must be an object.'] },
    };
  }
  const errors = {};
  const allowed = Object.keys(DEFAULT_SALON_BUTTON_STYLE);
  addUnknownFields(errors, draft, allowed);
  const value = { ...DEFAULT_SALON_BUTTON_STYLE, ...draft };
  Object.entries(SALON_PUBLIC_LAYOUT_REGISTRY.buttons).forEach(([
    field,
    choices,
  ]) => {
    if (!choices.includes(value[field])) {
      errors[field] = ['Select a supported layout value.'];
    }
  });
  Object.entries(BUTTON_STYLE_REGISTRY).forEach(([field, choices]) => {
    if (!choices.includes(value[field])) {
      errors[field] = ['Select a supported style value.'];
    }
  });
  ['background_color', 'border_color', 'text_color'].forEach((field) => {
    const color = normalizeColor(value[field]);
    if (color.error) errors[field] = [color.error];
    else value[field] = color.value;
  });
  return { value, errors };
};

const validateContent = (draft) => {
  const result = validateChoiceFields(
    draft,
    DEFAULT_SALON_PUBLIC_LAYOUT.content,
    SALON_PUBLIC_LAYOUT_REGISTRY.content,
    'Select a supported layout value.',
  );
  if (!result.value) return result;

  const columns = result.value.service_columns;
  if (
    typeof columns !== 'number'
    || !Number.isInteger(columns)
    || !SALON_SERVICE_COLUMN_OPTIONS.includes(columns)
  ) {
    result.errors.service_columns = ['Select 2, 3, or 4 columns.'];
  }
  return result;
};

const validateSectionStyles = (draft) => {
  if (!isObject(draft)) {
    return {
      value: null,
      errors: { non_field_errors: ['This section must be an object.'] },
    };
  }
  const errors = {};
  addUnknownFields(errors, draft, SALON_SECTION_STYLE_KEYS);
  const value = {};
  SALON_SECTION_STYLE_KEYS.forEach((sectionKey) => {
    const sectionDraft = draft[sectionKey] ?? {};
    if (!isObject(sectionDraft)) {
      errors[sectionKey] = {
        non_field_errors: ['This section must be an object.'],
      };
      return;
    }
    const sectionErrors = {};
    addUnknownFields(sectionErrors, sectionDraft, ['title', 'content']);
    const title = validateTextStyle(
      sectionDraft.title ?? {},
      DEFAULT_SALON_TITLE_STYLE,
    );
    const content = validateTextStyle(
      sectionDraft.content ?? {},
      DEFAULT_SALON_CONTENT_STYLE,
    );
    if (Object.keys(title.errors).length) sectionErrors.title = title.errors;
    if (Object.keys(content.errors).length) {
      sectionErrors.content = content.errors;
    }
    if (Object.keys(sectionErrors).length) errors[sectionKey] = sectionErrors;
    value[sectionKey] = { title: title.value, content: content.value };
  });
  return { value, errors };
};

const validateVisibility = (draft = {}) => {
  if (!isObject(draft)) {
    return {
      value: null,
      errors: { non_field_errors: ['This section must be an object.'] },
    };
  }
  const errors = {};
  addUnknownFields(errors, draft, SALON_SECTION_VISIBILITY_KEYS);
  const value = { ...DEFAULT_SALON_SECTION_VISIBILITY, ...draft };
  SALON_SECTION_VISIBILITY_KEYS.forEach((field) => {
    if (typeof value[field] !== 'boolean') {
      errors[field] = ['Enter a valid boolean value.'];
    }
  });
  return { value, errors };
};

const migrateLegacyLayout = (config) => {
  const errors = {};
  addUnknownFields(errors, config, ['version', 'buttons', 'header', 'content']);
  const value = copyDefaultLayout();
  Object.entries(SALON_PUBLIC_LAYOUT_REGISTRY).forEach(([section, fields]) => {
    const result = validateChoiceFields(
      config[section] ?? {},
      DEFAULT_SALON_PUBLIC_LAYOUT[section],
      fields,
      'Select a supported layout value.',
    );
    if (Object.keys(result.errors).length) errors[section] = result.errors;
    else value[section] = { ...value[section], ...result.value };
  });
  return { value, errors };
};

const migrateVersionTwoLayout = (config) => {
  const errors = {};
  addUnknownFields(
    errors,
    config,
    ['version', 'buttons', 'header', 'content', 'section_styles'],
  );
  if (Object.keys(errors).length) {
    return { isValid: false, value: null, errors };
  }
  return validateSalonPublicLayoutDraft({
    ...config,
    version: 3,
    visibility: { ...DEFAULT_SALON_SECTION_VISIBILITY },
  });
};

export const validateSalonPublicLayoutDraft = (config) => {
  if (!isObject(config)) {
    return {
      isValid: false,
      value: null,
      errors: {
        non_field_errors: ['Layout configuration must be an object.'],
      },
    };
  }

  if (config.version === 1) {
    const legacy = migrateLegacyLayout(config);
    if (Object.keys(legacy.errors).length) {
      return { isValid: false, value: null, errors: legacy.errors };
    }
    return { isValid: true, value: legacy.value, errors: {} };
  }

  if (config.version === 2) {
    return migrateVersionTwoLayout(config);
  }

  const errors = {};
  addUnknownFields(errors, config, ALLOWED_TOP_LEVEL_KEYS);
  if (
    Object.prototype.hasOwnProperty.call(config, 'version')
    && config.version !== 3
  ) {
    errors.version = ['Only layout versions 1, 2, and 3 are supported.'];
  }

  const value = copyDefaultLayout();
  const buttons = validateButtons(config.buttons ?? {});
  if (Object.keys(buttons.errors).length) errors.buttons = buttons.errors;
  else value.buttons = buttons.value;

  const header = validateChoiceFields(
    config.header ?? {},
    DEFAULT_SALON_PUBLIC_LAYOUT.header,
    SALON_PUBLIC_LAYOUT_REGISTRY.header,
    'Select a supported layout value.',
  );
  if (Object.keys(header.errors).length) errors.header = header.errors;
  else value.header = header.value;

  const content = validateContent(config.content ?? {});
  if (Object.keys(content.errors).length) errors.content = content.errors;
  else value.content = content.value;

  const sectionStyles = validateSectionStyles(config.section_styles ?? {});
  if (Object.keys(sectionStyles.errors).length) {
    errors.section_styles = sectionStyles.errors;
  } else {
    value.section_styles = sectionStyles.value;
  }

  const visibility = validateVisibility(config.visibility ?? {});
  if (Object.keys(visibility.errors).length) {
    errors.visibility = visibility.errors;
  } else {
    value.visibility = visibility.value;
  }

  if (Object.keys(errors).length) {
    return { isValid: false, value: null, errors };
  }
  return { isValid: true, value, errors: {} };
};

export const resolveSalonPublicLayout = (stored) => {
  const validation = validateSalonPublicLayoutDraft(stored ?? {});
  return validation.isValid ? validation.value : copyDefaultLayout();
};

export const buildSalonTextStyleOverrides = (
  style,
  defaults,
  direction = 'ltr',
) => {
  const overrides = {};
  if (style.font_key !== defaults.font_key) {
    overrides.fontFamily = SALON_FONT_PRESETS[style.font_key][direction]
      .join(', ');
  }
  if (style.size !== defaults.size) {
    overrides.fontSize = TEXT_SIZE_TOKENS[style.size];
  }
  if (style.weight !== defaults.weight) overrides.fontWeight = style.weight;
  if (style.italic !== defaults.italic) {
    overrides.fontStyle = style.italic ? 'italic' : 'normal';
  }
  if (style.color !== defaults.color) overrides.color = style.color;
  if (style.alignment !== defaults.alignment) {
    overrides.textAlign = style.alignment;
  }
  if (style.transform !== defaults.transform) {
    overrides.textTransform = style.transform;
  }
  if (style.letter_spacing !== defaults.letter_spacing) {
    overrides.letterSpacing = LETTER_SPACING_TOKENS[style.letter_spacing];
  }
  return overrides;
};

const buildButtonStyleOverrides = (buttons, direction) => {
  const overrides = {};
  if (buttons.font_key !== DEFAULT_SALON_BUTTON_STYLE.font_key) {
    overrides.fontFamily = SALON_FONT_PRESETS[buttons.font_key][direction]
      .join(', ');
  }
  if (buttons.text_size !== DEFAULT_SALON_BUTTON_STYLE.text_size) {
    overrides.fontSize = TEXT_SIZE_TOKENS[buttons.text_size];
  }
  if (buttons.weight !== DEFAULT_SALON_BUTTON_STYLE.weight) {
    overrides.fontWeight = buttons.weight;
  }
  if (buttons.italic !== DEFAULT_SALON_BUTTON_STYLE.italic) {
    overrides.fontStyle = buttons.italic ? 'italic' : 'normal';
  }
  if (buttons.transform !== DEFAULT_SALON_BUTTON_STYLE.transform) {
    overrides.textTransform = buttons.transform;
  }
  if (
    buttons.letter_spacing !== DEFAULT_SALON_BUTTON_STYLE.letter_spacing
  ) {
    overrides.letterSpacing = LETTER_SPACING_TOKENS[
      buttons.letter_spacing
    ];
  }
  return overrides;
};

export const buildSalonPublicLayout = (config, direction = 'ltr') => {
  const resolvedConfig = resolveSalonPublicLayout(config);
  const normalizedDirection = direction === 'rtl' ? 'rtl' : 'ltr';
  const buttonAppearance = BUTTON_APPEARANCE_TOKENS[
    resolvedConfig.buttons.appearance
  ];
  const buttonShape = BUTTON_SHAPE_TOKENS[resolvedConfig.buttons.shape];
  const buttonSize = BUTTON_SIZE_TOKENS[resolvedConfig.buttons.size];

  const sectionStyles = Object.fromEntries(
    SALON_SECTION_STYLE_KEYS.map((sectionKey) => {
      const section = resolvedConfig.section_styles[sectionKey];
      return [sectionKey, {
        title: {
          ...section.title,
          styleOverrides: buildSalonTextStyleOverrides(
            section.title,
            DEFAULT_SALON_TITLE_STYLE,
            normalizedDirection,
          ),
        },
        content: {
          ...section.content,
          styleOverrides: buildSalonTextStyleOverrides(
            section.content,
            DEFAULT_SALON_CONTENT_STYLE,
            normalizedDirection,
          ),
        },
      }];
    }),
  );

  return {
    config: resolvedConfig,
    tokens: {
      direction: normalizedDirection,
      buttons: {
        ...resolvedConfig.buttons,
        ...buttonAppearance,
        ...buttonShape,
        ...buttonSize,
        backgroundColor: resolvedConfig.buttons.background_color,
        borderColor: resolvedConfig.buttons.border_color,
        textColor: resolvedConfig.buttons.text_color,
        styleOverrides: buildButtonStyleOverrides(
          resolvedConfig.buttons,
          normalizedDirection,
        ),
      },
      header: {
        ...resolvedConfig.header,
        mode: resolvedConfig.header.composition,
        position: resolvedConfig.header.behavior === 'sticky'
          ? 'sticky'
          : 'static',
        isOverlay: resolvedConfig.header.surface === 'transparent',
      },
      content: {
        ...resolvedConfig.content,
        textAlign: resolvedConfig.content.alignment,
        serviceMode: resolvedConfig.content.services,
        serviceColumns: resolvedConfig.content.service_columns,
        sidebarMode: resolvedConfig.content.sidebar,
      },
      visibility: { ...resolvedConfig.visibility },
      sectionStyles,
    },
  };
};
